import {QueryClient, useQueryClient} from '@github-ui/react-query'
import {useMemo, useCallback} from 'react'
import type {IdToPullRequestMap} from '../types/pull'
import type {Session} from '../types/session'
import type {BaseTask, HydratedTask, OptimisticTask, UITask, UnhydratedTask} from '../types/task'
import {fetchCAPI} from '../utils/fetch-capi'
import {mergeServerTasksWithOptimistic} from '../utils/task-helpers'
import {useCAPIQuery} from './useCAPIQuery'
import {useLocalStorageCache} from './useLocalStorageCache'
import {usePullRequests} from './usePullRequests'
import {useAgentSessionsAlive} from './use-agent-sessions-alive'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useDebounce} from '@github-ui/use-debounce'

export const agentTasksQueryKey = ['agent-tasks']
export const TASKS_STORAGE_KEY = 'AGENT_SESSIONS_TASKS'

function collateSessionsIntoTasks(sessions: Session[]): UnhydratedTask[] {
  const resourceIdToTaskMap = new Map<string, UnhydratedTask>()

  for (const session of sessions) {
    const task = resourceIdToTaskMap.get(session.resource_id.toString())
    if (task) {
      task.session_count += 1
      if (new Date(session.last_updated_at) > new Date(task.latest_session_updated_at)) {
        task.latest_session_updated_at = session.last_updated_at
        task.latest_session_state = session.state
        task.latest_session_id = session.id
        task.agentId = session.agent_id?.toString()
      }
      if (session.event_identifiers) {
        // Include all event_identifiers to enable task de-duping with cached sessions
        const existingIdentifiers = new Set(task.event_identifiers || [])
        const newIdentifiers = session.event_identifiers.filter(id => !existingIdentifiers.has(id))
        task.event_identifiers = [...(task.event_identifiers || []), ...newIdentifiers]
      }
    } else {
      resourceIdToTaskMap.set(session.resource_id.toString(), {
        resource_type: session.resource_type,
        resource_id: session.resource_id,
        resource_global_id: session.resource_global_id,
        session_count: 1,
        latest_session_updated_at: session.last_updated_at,
        latest_session_state: session.state,
        latest_session_id: session.id,
        event_identifiers: session.event_identifiers,
        event_type: session.event_type ?? undefined,
        agentId: session.agent_id?.toString(),
        task_id: session.task_id,
        creator_id: session.owner_id,
      })
    }
  }

  return Array.from(resourceIdToTaskMap.values()).sort((a, b) => {
    return new Date(b.latest_session_updated_at).getTime() - new Date(a.latest_session_updated_at).getTime()
  })
}

export const fetchTasks = async ({
  queryClient,
  setCachedTasks,
  copilotApiUrl,
}: {
  queryClient: QueryClient
  setCachedTasks: (tasks: BaseTask[]) => void
  copilotApiUrl?: string | null
}) => {
  const response = await fetchCAPI({
    path: `/agents/sessions`,
    copilotApiUrl,
  })
  const data = await response.json()
  const sessions = data.sessions
  const serverTasks = collateSessionsIntoTasks(sessions as Session[])
  // Get existing tasks from cache so that we can find and preserve optimistic tasks until next poll
  const existingTasks = queryClient.getQueryData<BaseTask[]>(agentTasksQueryKey) || []

  // Merge server tasks with existing optimistic tasks
  const optimisticTasks = existingTasks.filter(task => task.isOptimistic === true) as OptimisticTask[]
  const mergedTasks = mergeServerTasksWithOptimistic(serverTasks, optimisticTasks)

  // Cache the merged tasks to localStorage
  setCachedTasks(mergedTasks)

  return mergedTasks
}

export function useTasks({
  sessionsPollingInterval = 3000,
  initialTasks,
  initialPullRequestsMap,
}: {
  sessionsPollingInterval?: number | false
  initialTasks?: HydratedTask[]
  initialPullRequestsMap?: IdToPullRequestMap
} = {}) {
  const {value: cachedTasks, setValue: setCachedTasks} = useLocalStorageCache<BaseTask[]>(TASKS_STORAGE_KEY)
  const queryClient = useQueryClient()
  const isAliveEnabled = isFeatureEnabled('copilot_agent_sessions_alive_updates')

  const handleAliveUpdate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: agentTasksQueryKey,
    })
    queryClient.invalidateQueries({
      queryKey: ['agent-sessions-pull-requests'],
    })
  }, [queryClient])

  const debouncedHandleAliveUpdate = useDebounce(
    handleAliveUpdate,
    sessionsPollingInterval ? sessionsPollingInterval : 3000,
    {maxWait: 10000},
  )

  // Subscribe to alive updates if feature flag is enabled
  useAgentSessionsAlive({
    onEvent: debouncedHandleAliveUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: true,
    enableLogUpdates: false,
    enabled: isAliveEnabled,
  })

  const query = useCAPIQuery<BaseTask[]>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: agentTasksQueryKey,
    queryFn: async () =>
      await fetchTasks({
        queryClient,
        setCachedTasks,
      }),
    initialData: initialTasks ?? cachedTasks,
    refetchInterval: isAliveEnabled ? false : sessionsPollingInterval,
    refetchIntervalInBackground: false,
  })

  // Get all tasks from the query result (already includes optimistic tasks)
  const allTasks = useMemo(() => query.data || [], [query.data])

  // Get pull requests for tasks that have resource IDs (not optimistic)
  const tasksWithResourceIds = allTasks.filter(task => task.resource_id !== undefined)
  const {
    data: idToPullRequestMap,
    isError: isErrorPullRequests,
    isLoading: isLoadingPullRequests,
  } = usePullRequests({
    tasks: tasksWithResourceIds,
    sessionsPollingInterval,
    initialPullRequestsMap,
  })

  // Attach pull request data to tasks where available, but only return hydrated or optimistic tasks
  const returnTasks: UITask[] = allTasks
    .map(task => {
      if (task.resource_id === undefined) {
        // If the task doesn't have a resource_id, it can't be linked to a pull request
        return task
      }

      const pull = idToPullRequestMap[task.resource_id]

      if (pull) {
        return {
          ...task,
          isOptimistic: false, //Be sure to always mark any transitional tasks that have pull requests as no longer optimistic
          pull,
        }
      } else {
        // If no pull request is found, return the task without pull data
        return {
          ...task,
          pull: undefined,
        }
      }
    })
    .filter(task => {
      // Always include optimistic tasks
      if (task.isOptimistic === true) {
        return true
      }

      // Only include other server tasks that have been hydrated with pull request data
      return task.pull !== undefined
    }) as UITask[]

  // Show loading when either tasks or pull requests are loading (for initial load)
  const isLoading = (query.isLoading && !query.data) || (isLoadingPullRequests && !initialPullRequestsMap)

  const isError = query.isError || isErrorPullRequests

  return {
    tasks: returnTasks,
    isLoading,
    isError,
  }
}
